/* Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { GraphQLUpload } from "graphql-upload-minimal";
import {
  ActionScope,
  CreateActionInput,
  useCreateCustomActionMutation,
  AzureFunctionMethod,
  ActionType,
  WebhookActionMethod,
} from "~/generated/graphql";
import { showError } from "~/services/toast";
import { CustomActionData } from "../../CustomActionCreate/components/CustomActionCreateWizard/constants";

type Props = {
  onSuccess: (id: string) => void;
};

export function useCreateCustomAction({ onSuccess }: Props) {
  const [createCustomActionMutation, { loading }] =
    useCreateCustomActionMutation({
      onCompleted: (data) => {
        if (data.createAction) {
          onSuccess(data?.createAction.id);
        }
      },
      onError: (error) => {
        showError(error.message);
      },
      update(cache, { data }, { variables }) {
        if (data?.createAction && variables?.input) {
          cache.modify({
            fields: {
              actions: (existing) => {
                return {
                  cloudAccounts: [existing, variables.input],
                };
              },
            },
          });

          cache.gc();
        }
      },
      context: {
        headers: {
          "apollo-require-preflight": "true",
        },
      },
    });

  async function createCustomAction(data: CustomActionData) {
    const prepareData = mapCustomActionData(data);

    createCustomActionMutation({
      variables: {
        input: prepareData,
      },
    });
  }

  return { createCustomAction, loading } as const;
}

const getTypeProperties = (data: CustomActionData) => {
  switch (data.settings?.type) {
    case ActionType.AwsLambda:
      return {
        awsLambda: {
          lambdaArn: data.settings?.lambdaArn ?? "",
        },
      };
    case ActionType.AzureFunction:
      return {
        azureFunction: {
          accessKeyId: data.settings?.accessKeyId ?? "",
          functionUrl: data.settings?.functionUrl ?? "",
          functionMethod:
            data.settings?.functionMethod ?? AzureFunctionMethod.Get,
        },
      };
    case ActionType.Script:
      return {
        script: {
          file: data.settings?.script as typeof GraphQLUpload,
        },
      };
    case ActionType.SsmDocument:
      return {
        ssmDocument: {
          documentId: data.settings?.documentId ?? "",
        },
      };
    case ActionType.Webhook:
      return {
        webhook: {
          webhookUrl: data.settings?.webhookUrl ?? "",
          payload: data.settings?.payload ?? "",
          headers: data.settings?.headers ?? [],
          webhookMethod:
            data.settings?.webhookMethod ?? WebhookActionMethod.Get,
        },
      };
    default:
      throw new Error("unsupported action type");
  }
};

export function mapCustomActionData(data: CustomActionData): CreateActionInput {
  return {
    color: data.details?.color ?? "",
    name: data.details?.name ?? "",
    description: data.details?.description,
    type: data.settings?.type ?? ActionType.AwsLambda,
    scope: data.settings?.scope ?? ActionScope.Plan,
    typeProperties: {
      ...getTypeProperties(data),
    },
    parameterDefinitions: [],
    synchronous: true,
  };
}
